import React, { useEffect, useState } from 'react';
import { UserPlus, Check, X, Clock, Building2 } from 'lucide-react';
import { adminApi } from '../../lib/api';

type CollabRequest = {
    id: string;
    client_id: string;
    company_name?: string;
    full_name?: string;
    username: string;
    email: string;
    role?: string;
    status: 'pending' | 'accepted' | 'refused';
    refusal_reason?: string;
    modules_access?: string;
    created_at: string;
};

const MODULE_LABELS: Record<string, string> = {
    planning: 'Planning',
    evenementiel: 'Événementiel',
    facture: 'Facture',
    crm: 'CRM',
    employes: 'Postes & Employés',
    rh: 'Demande de congé(s)',
    exception_service: "Service d'exception",
    suivi_privatisation: 'Suivi privatisation',
    menus_convives: 'Menus & Convives',
    menu_plats: 'Menu & Plats',
    mode_terrain: 'Mode terrain',
};

const parseModules = (raw?: string): string[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    pending: { label: 'En attente', color: 'bg-warning-500/10 text-warning-500 border-warning-500/20' },
    accepted: { label: 'Acceptée', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    refused: { label: 'Refusée', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

const formatDate = (d?: string) => {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return d;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const AdminCollaboratorRequests: React.FC = () => {
    const [requests, setRequests] = useState<CollabRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [refusingId, setRefusingId] = useState<string | null>(null);
    const [refuseReason, setRefuseReason] = useState('');
    const [error, setError] = useState('');

    const load = async () => {
        try {
            const data = await adminApi.getCollaboratorRequests();
            if (Array.isArray(data)) setRequests(data);
        } catch (e) {
            console.error('Failed to load collaborator requests', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleAccept = async (req: CollabRequest) => {
        if (!confirm(`Accepter la demande de ${req.full_name || req.username} et créer le compte collaborateur ?`)) return;
        setProcessingId(req.id);
        setError('');
        try {
            await adminApi.acceptCollaboratorRequest(req.id);
            window.dispatchEvent(new Event('collaboratorRequestProcessed'));
            await load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'acceptation de la demande.");
        } finally {
            setProcessingId(null);
        }
    };

    const handleOpenRefuse = (req: CollabRequest) => {
        setRefusingId(req.id);
        setRefuseReason('');
        setError('');
    };

    const handleConfirmRefuse = async (req: CollabRequest) => {
        if (!refuseReason.trim()) {
            setError('Un motif de refus est requis.');
            return;
        }
        setProcessingId(req.id);
        setError('');
        try {
            await adminApi.refuseCollaboratorRequest(req.id, refuseReason.trim());
            window.dispatchEvent(new Event('collaboratorRequestProcessed'));
            setRefusingId(null);
            await load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du refus de la demande.');
        } finally {
            setProcessingId(null);
        }
    };

    const pending = requests.filter((r) => r.status === 'pending');
    const processed = requests.filter((r) => r.status !== 'pending');

    return (
        <div className="space-y-8">
            <header className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
                    <UserPlus size={22} />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-content">Demandes de comptes collaborateur</h1>
                    <p className="text-sm text-content-muted mt-1">Validez ou refusez les demandes soumises par vos clients.</p>
                </div>
            </header>

            {error && <p className="text-sm text-red-400">{error}</p>}

            {loading ? (
                <p className="text-content-muted text-sm">Chargement...</p>
            ) : (
                <>
                    <section className="space-y-4">
                        <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest">En attente ({pending.length})</h2>
                        {pending.length === 0 ? (
                            <div className="text-center py-12 border border-dashed border-border rounded-3xl bg-surface/40">
                                <Clock size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                                <p className="text-content-muted text-sm">Aucune demande en attente.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {pending.map((req) => (
                                    <div key={req.id} className="bg-surface border border-border rounded-2xl p-5">
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-bold text-content">{req.full_name || req.username}</span>
                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${STATUS_LABELS[req.status].color}`}>{STATUS_LABELS[req.status].label}</span>
                                                </div>
                                                <p className="text-xs text-content-muted flex items-center gap-1.5">
                                                    <Building2 size={12} /> {req.company_name || 'Client inconnu'}
                                                </p>
                                                <p className="text-xs text-content-muted">
                                                    Pseudo : <span className="font-mono text-content">{req.username}</span> · Email : {req.email}{req.role ? <> · Rôle : {req.role}</> : null}
                                                </p>
                                                {parseModules(req.modules_access).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 pt-1">
                                                        {parseModules(req.modules_access).map((m) => (
                                                            <span key={m} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20">
                                                                {MODULE_LABELS[m] || m}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                <p className="text-[11px] text-content-muted">Soumise le {formatDate(req.created_at)}</p>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    onClick={() => handleAccept(req)}
                                                    disabled={processingId === req.id}
                                                    className="flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-emerald-500/20 disabled:opacity-50"
                                                >
                                                    <Check size={14} /> Accepter
                                                </button>
                                                <button
                                                    onClick={() => handleOpenRefuse(req)}
                                                    disabled={processingId === req.id}
                                                    className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-red-500/20 disabled:opacity-50"
                                                >
                                                    <X size={14} /> Refuser
                                                </button>
                                            </div>
                                        </div>

                                        {refusingId === req.id && (
                                            <div className="mt-4 pt-4 border-t border-border space-y-3">
                                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Motif du refus</label>
                                                <textarea
                                                    value={refuseReason}
                                                    onChange={(e) => setRefuseReason(e.target.value)}
                                                    rows={3}
                                                    className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-content outline-none focus:border-brand-500 transition-colors text-sm resize-none"
                                                    placeholder="Expliquez au client pourquoi cette demande est refusée..."
                                                />
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => setRefusingId(null)} className="px-4 py-2 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold transition-all">
                                                        Annuler
                                                    </button>
                                                    <button
                                                        onClick={() => handleConfirmRefuse(req)}
                                                        disabled={processingId === req.id}
                                                        className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-all disabled:opacity-50"
                                                    >
                                                        Confirmer le refus
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {processed.length > 0 && (
                        <section className="space-y-4">
                            <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest">Historique</h2>
                            <div className="space-y-2">
                                {processed.map((req) => (
                                    <div key={req.id} className="bg-surface/60 border border-border rounded-2xl p-4">
                                        <div className="flex items-center justify-between gap-4 flex-wrap">
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-bold text-content text-sm">{req.full_name || req.username}</span>
                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${STATUS_LABELS[req.status].color}`}>{STATUS_LABELS[req.status].label}</span>
                                                </div>
                                                <p className="text-xs text-content-muted">{req.company_name || 'Client inconnu'} · {req.email}</p>
                                                {req.status === 'refused' && req.refusal_reason && (
                                                    <p className="text-xs text-content-muted italic mt-1">Motif : {req.refusal_reason}</p>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-content-muted">{formatDate(req.created_at)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
};
